import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { updatedUser } from "../../../api/manageUser";
import { getUsers } from "../../../slices/userSlice";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";

const EditUser = ({ user, onClose }) => {
  const dispatch = useDispatch();

  // Options de rôles avec des valeurs numériques
  const roleOptions = [
    { value: 1, label: 'Serveur' },
    { value: 2, label: 'Cuisiner' },
    { value: 3, label: 'Barman' },
    { value: 4, label: 'Manager' },
    { value: 5, label: 'Caissier' },
    { value: 6, label: 'Econome' },
  ];

  // Initialisation des champs avec une protection contre user indéfini
  const [username, setUsername] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const initialRole = roleOptions.find(option => option.value === user?.role?.id) || null;
  const [role, setRole] = useState(initialRole);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  // États pour la visibilité des mots de passe
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // États pour le chargement et les erreurs
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Mettre à jour les champs lorsque user change
  useEffect(() => {
    setUsername(user?.name || '');
    setEmail(user?.email || '');
    const selectedRoleOption = roleOptions.find(option => option.value === user?.role?.id) || null;
    setRole(selectedRoleOption);
  }, [user]);

  // Fonctions pour basculer la visibilité du mot de passe
  const handleTogglePassword = () => setShowPassword(prev => !prev);
  const handleToggleConfirmPassword = () => setShowConfirmPassword(prev => !prev);

  // Gestion du submit du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !email || !role) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    if (password) {
      if (password !== passwordConfirmation) {
        setError('Les mots de passe ne correspondent pas.');
        return;
      }
      if (password.length < 8) {
        setError('Le mot de passe doit contenir au moins 8 caractères.');
        return;
      }
    }

    setLoading(true);

    const userData = {
      id: user.id,
      name: username,
      email,
      role_id: role.value,
      ...(password ? { password, password_confirmation: passwordConfirmation } : {}),
    };

    try {
      const response = await updatedUser(userData);
      const users = response.data.users;
      dispatch(getUsers(users));

      Swal.fire(
        "Modifié !",
        `${username} a été mis à jour avec succès en tant que ${role.label}.`,
        "success"
      );

      setUsername('');
      setEmail('');
      setRole(null);
      setPassword('');
      setPasswordConfirmation('');
      setError('');
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la mise à jour de l'utilisateur.");
    }
    setLoading(false);
  };

  return (
    <div>
      {/* Modal d'édition d'utilisateur */}
      <div className="modal fade" id="edit-units">
        <div className="modal-dialog modal-dialog-centered custom-modal-two">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header">
                  <div className="page-title">
                    <h4>Edition</h4>
                  </div>
                  <button type="button" className="close" data-bs-dismiss="modal" aria-label="Close">
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body custom-modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      {/* Nom d'utilisateur */}
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Nom d&apos;utilisateur</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Entrez un pseudo"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                          />
                        </div>
                      </div>
                      {/* Email */}
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Email</label>
                          <input
                            type="email"
                            className="form-control"
                            placeholder="Ex: geaorge@gmail.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </div>
                      </div>
                      {/* Sélection du rôle */}
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Role</label>
                          <Select
                            classNamePrefix="react-select"
                            options={roleOptions}
                            placeholder="Choisissez un rôle"
                            value={role}
                            onChange={setRole}
                          />
                        </div>
                      </div>
                      {/* Mot de passe */}
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Mot de passe</label>
                          <div className="pass-group">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              className="pass-input"
                              placeholder="Entrer un mot de passe (optionnel)"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                            />
                            <span
                              className={`fas toggle-password ${showPassword ? 'fa-eye' : 'fa-eye-slash'}`}
                              onClick={handleTogglePassword}
                            />
                          </div>
                        </div>
                      </div>
                      {/* Confirmation du mot de passe */}
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Confirmation du mot de passe</label>
                          <div className="pass-group">
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              className="pass-input"
                              placeholder="Confirmez le mot de passe (optionnel)"
                              value={passwordConfirmation}
                              onChange={(e) => setPasswordConfirmation(e.target.value)}
                            />
                            <span
                              className={`fas toggle-password ${showConfirmPassword ? 'fa-eye' : 'fa-eye-slash'}`}
                              onClick={handleToggleConfirmPassword}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {error && <p className="text-danger">{error}</p>}
                    <div className="modal-footer-btn">
                      <button type="button" className="btn btn-cancel me-2" data-bs-dismiss="modal">
                        Retour
                      </button>
                      <button type="submit" className="btn btn-submit" disabled={loading}>
                        {loading ? "Modification en cours..." : "Modifier"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Modal d'édition d'utilisateur */}
    </div>
  );
};

EditUser.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    email: PropTypes.string,
    role: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EditUser;
